import React, { useState } from 'react'
import { gql, useLazyQuery, useQuery } from '@apollo/client'
import {
  Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import { T } from '../PartnerSignups/onboardingDesign'
import { Badge, GhostButton, KV, LinkButton, Notice } from '../PartnerSignups/onboardingUi'
import { ReferralsTable, headCell, money, pct, tabSx, tableSx } from './PortalParts'
import { ISO_AGENT_FIELDS, MonthAccounts, PlatformStats, RatesTable, STATEMENT_FIELDS, StatementsList, bonusText } from './isoParts'
import { timeAgo } from '../../utils/authToken'

/**
 * Super admin: one ISO up close (resolvers/iso.js): its agents, the accounts
 * in its network, this month so far and past months, its rates, and the full
 * bank and tax numbers to send a payment (shown on request, logged).
 */

const DETAILS = gql`
  query IsoDetails($isoId: ID!) {
    isoAgents(isoId: $isoId) { ${ISO_AGENT_FIELDS} }
    isoPortfolio(isoId: $isoId) {
      _id agentId agentName restaurantName contactEmail businessType status startedAt signedAt approvedAt commissionAmount commissionStatus pricing held
    }
    isoStatements(isoId: $isoId) { ${STATEMENT_FIELDS} }
  }
`
const PAYOUT_DETAILS = gql`
  query IsoPayoutDetails($isoId: ID!, $agentId: ID) {
    isoPayoutDetails(isoId: $isoId, agentId: $agentId) { name bankName holderName accountType routing account taxIdType taxId }
  }
`

const ACCOUNT_TYPE = { BUSINESS_CHECKING: 'Business checking', CHECKING: 'Personal checking' }

function PaymentDetails({ isoId, agentId, label }) {
  const [load, { data, loading, error }] = useLazyQuery(PAYOUT_DETAILS, { fetchPolicy: 'network-only' })
  const d = data && data.isoPayoutDetails
  if (!d) {
    return (
      <Box>
        {error && <Notice severity="error">{error.message}</Notice>}
        <LinkButton onClick={() => load({ variables: { isoId, agentId: agentId || null } })} disabled={loading} sx={{ ml: -1 }}>
          {loading ? 'Loading…' : label}
        </LinkButton>
      </Box>
    )
  }
  return (
    <Box sx={{ p: 2, borderRadius: '14px', background: T.footerBg, border: `1px solid ${T.divider}` }}>
      <KV rows={[
        ['Pay to', d.name],
        ['Bank', d.bankName],
        ['Name on the account', d.holderName],
        ['Account type', ACCOUNT_TYPE[d.accountType] || d.accountType],
        ['Routing number', d.routing],
        ['Account number', d.account],
        ...(agentId ? [] : [[d.taxIdType === 'SSN' ? 'SSN' : 'EIN', d.taxId]]),
      ]}
      />
    </Box>
  )
}

export default function IsoDetailsDialog({ iso, onClose }) {
  const [tab, setTab] = useState(0)
  const { data, loading, error } = useQuery(DETAILS, { variables: { isoId: iso._id }, fetchPolicy: 'network-only' })
  const agents = (data && data.isoAgents) || []
  const portfolio = (data && data.isoPortfolio) || []
  const statements = (data && data.isoStatements) || []
  const month = iso.thisMonth

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {iso.corporateName}
        <Typography sx={{ color: T.muted, fontSize: 14 }}>
          {[iso.email, iso.phone, [iso.city, iso.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: T.divider, p: 0 }}>
        <Tabs
          value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile
          sx={{ borderBottom: `1px solid ${T.divider}`, px: { xs: 1, sm: 2 }, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' } }}
        >
          <Tab label={`Agents (${agents.length})`} sx={tabSx} />
          <Tab label={`Accounts (${portfolio.length})`} sx={tabSx} />
          <Tab label="This month" sx={tabSx} />
          <Tab label={`Past months (${statements.length})`} sx={tabSx} />
          <Tab label="Rates & payment" sx={tabSx} />
        </Tabs>
        <Box sx={{ overflowX: 'auto' }}>
          {loading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: T.red }} /></Box>
            : error ? <Notice severity="error" sx={{ m: 2 }}>{error.message}</Notice>
              : tab === 0 ? (
                agents.length === 0 ? <Typography sx={{ p: 5, textAlign: 'center', color: T.hint }}>The ISO hasn’t added any agents yet. It can sign accounts itself with its own link.</Typography> : (
                  <Table size="small" sx={{ minWidth: 900, ...tableSx }}>
                    <TableHead>
                      <TableRow>
                        {['Agent', 'Link code', 'Split', 'Bonus', 'Restaurants', 'Started', 'Approved', 'Owed', 'Paid', 'Bank for direct pay'].map(h => <TableCell key={h} sx={headCell}>{h}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {agents.map(a => (
                        <TableRow key={a._id} sx={{ opacity: a.isActive ? 1 : 0.55 }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: T.text }}>{a.name}</Typography>
                            <Typography variant="caption" sx={{ color: T.muted, fontSize: 13 }}>{a.email}</Typography>
                            {!a.isActive && <Badge kind="red" sx={{ ml: 1, fontSize: 11.5, px: 1 }}>Deactivated</Badge>}
                            <Typography sx={{ color: a.lastLoginAt ? T.text2 : T.hint, fontSize: 12.5, mt: 0.25 }}>
                              {a.lastLoginAt ? `Last login ${timeAgo(a.lastLoginAt)}` : 'No sign-in recorded yet'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13.5, color: T.text2, whiteSpace: 'nowrap' }}>{a.code}</TableCell>
                          <TableCell>{pct(a.split)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{bonusText(a.bonus, a.bonusOrders)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {a.stats ? a.stats.restaurants : 0}
                            <Typography sx={{ color: T.hint, fontSize: 12.5 }}>{a.stats ? a.stats.signedUp : 0} signed up · {a.stats ? a.stats.assigned : 0} assigned</Typography>
                          </TableCell>
                          <TableCell>{a.stats ? a.stats.started : 0}</TableCell>
                          <TableCell>{a.stats ? a.stats.approved : 0}</TableCell>
                          <TableCell sx={{ color: T.amberText, fontWeight: 600 }}>{money(a.stats && a.stats.pendingAmount)}</TableCell>
                          <TableCell>{money(a.stats && a.stats.paidAmount)}</TableCell>
                          <TableCell sx={{ minWidth: 200 }}>
                            {a.bankSet ? <PaymentDetails isoId={iso._id} agentId={a._id} label="Show bank details" /> : <Typography sx={{ color: T.hint, fontSize: 13 }}>Not entered</Typography>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              ) : tab === 1 ? (
                <ReferralsTable referrals={portfolio} agentColumn commissionHeader="Agent bonus" emptyText="No applications from this ISO or its agents yet." />
              ) : tab === 2 ? (
                <Box>
                  {month && (
                    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, display: 'flex', gap: 3, flexWrap: 'wrap', color: T.text2, fontSize: 14 }}>
                      <span>{month.label}: <strong>{money(month.gross)}</strong> earned on {month.orders} orders</span>
                      <span>Agents’ share <strong>{money(month.agentShares)}</strong> ({month.agentsPaidBy === 'RUSHMYFOOD' ? 'we pay them' : 'the ISO pays them'})</span>
                      <span>Owed to the ISO so far <strong>{money(month.isoAmount)}</strong></span>
                    </Box>
                  )}
                  {month && month.platforms && month.platforms.length > 0 && (
                    <Box sx={{ borderBottom: `1px solid ${T.divider}`, mb: 1 }}>
                      <PlatformStats month={month} />
                    </Box>
                  )}
                  <MonthAccounts accounts={month ? month.accounts : []} forAdmin />
                </Box>
              ) : tab === 3 ? (
                statements.length === 0 ? <Typography sx={{ p: 5, textAlign: 'center', color: T.hint }}>No finished months yet. Each month is added here after it ends.</Typography> : <StatementsList statements={statements} />
              ) : (
                <Box sx={{ p: { xs: 2, sm: 3 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 15.5, mb: 1 }}>What the ISO earns</Typography>
                    <RatesTable rates={iso.rates} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 15.5, mb: 1 }}>Payment</Typography>
                    <Typography sx={{ color: T.text2, fontSize: 14, mb: 1.5 }}>
                      {iso.payoutTo === 'AGENTS'
                        ? 'RushMyFood pays each agent their share directly, and the ISO the rest.'
                        : 'RushMyFood pays the ISO everything; the ISO pays its agents.'}
                    </Typography>
                    {iso.bank && iso.bank.isSet
                      ? <PaymentDetails isoId={iso._id} label={`Show full bank and tax details (${iso.bank.bankName || 'bank'} ${iso.bank.account})`} />
                      : <Notice severity="warning">No bank details yet. Add them with Edit.</Notice>}
                    <Typography sx={{ color: T.hint, fontSize: 12.5, mt: 1 }}>Showing full numbers is logged.</Typography>
                  </Box>
                </Box>
              )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <GhostButton onClick={onClose}>Close</GhostButton>
      </DialogActions>
    </Dialog>
  )
}
